import { forwardRef } from 'react';
import { splitByRanges, type FlagRange } from './flaggedRanges';

type Props = {
  text: string;
  ranges: FlagRange[];
};

/**
 * Mirror layer BEHIND the transparent textarea: same font, padding and
 * wrapping, text itself invisible, only the flagged values are painted
 * (soft tint + wavy underline). A textarea cannot style parts of its value,
 * so this is the standard way to mark words inside one. The composer keeps
 * the scroll positions in sync.
 */
export const FlaggedBackdrop = forwardRef<HTMLDivElement, Props>(function FlaggedBackdrop({ text, ranges }, ref) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="flagged-backdrop"
      className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words py-2 text-sm leading-relaxed text-transparent"
    >
      {splitByRanges(text, ranges).map((segment, index) =>
        segment.fact ? (
          <mark
            key={index}
            data-flag-kind={segment.fact.kind}
            className="rounded-[3px] bg-warning/15 text-transparent underline decoration-warning decoration-wavy decoration-[1.5px] underline-offset-[3px]"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
      {/* A trailing newline only takes up a line when something follows it. */}
      {'\u200b'}
    </div>
  );
});

export default FlaggedBackdrop;
